import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Globe2, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { MARKET_ZONES, COVERAGE_LEVELS } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";

const coverageMeta = (lang) => Object.fromEntries(COVERAGE_LEVELS.map((c) => [c.value, c]));
const coverageCls = { covered: "border-champagne bg-champagne/10 text-champagne", developing: "border-anthracite/30 bg-sand text-anthracite/70", on_demand: "border-tertiary bg-white text-anthracite/50" };

export default function MarketsList() {
  const { lang, L } = useLang();
  useSeo({
    title: lang === "fr" ? "Marchés" : "Markets",
    description: lang === "fr"
      ? "Intelligence de marché RSTI : couverture par zone, secteurs suivis, et contenus reliés (opportunités, projets, partenaires, experts, sourcing)."
      : "RSTI market intelligence: coverage by zone, tracked sectors, and linked content (opportunities, projects, partners, experts, sourcing).",
    lang,
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("");
  const cov = coverageMeta(lang);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (zone) params.zone = zone;
    api.get("/markets", { params }).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, [q, zone]);

  const byZone = MARKET_ZONES.map((z) => ({ zone: z, list: items.filter((m) => m.zone === z.value) })).filter((g) => g.list.length > 0);

  return (
    <div>
      <section className="border-b border-tertiary bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-16">
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI</span>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite sm:text-5xl">{lang === "fr" ? "Marchés" : "Markets"}</h1>
          <p className="mt-3 max-w-2xl text-anthracite/70">{lang === "fr"
            ? "Une couche d'intelligence qui relie chaque marché à l'écosystème RSTI — opportunités, projets, partenaires, experts et sourcing."
            : "An intelligence layer connecting each market to the RSTI ecosystem — opportunities, projects, partners, experts and sourcing."}</p>
          <p className="mt-4 font-serif text-lg italic text-anthracite/80" data-testid="markets-positioning">« {lang === "fr" ? "RSTI ne prétend pas tout savoir. RSTI sait trouver." : "RSTI does not claim to know everything. RSTI knows how to find."} »</p>

          <div className="mt-6 flex flex-wrap gap-3">
            {COVERAGE_LEVELS.map((c) => (
              <span key={c.value} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${coverageCls[c.value]}`}><span className="h-1.5 w-1.5 rounded-full bg-current" />{c[lang]} — <span className="opacity-70">{lang === "fr" ? c.descFr : c.descEn}</span></span>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <div className="relative min-w-[240px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-anthracite/40" />
              <input data-testid="markets-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "fr" ? "Rechercher un marché…" : "Search a market…"} className="w-full border border-tertiary bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-champagne" />
            </div>
            <select data-testid="filter-zone" value={zone} onChange={(e) => setZone(e.target.value)} className="border border-tertiary bg-white px-3 py-2.5 text-sm outline-none focus:border-champagne"><option value="">{lang === "fr" ? "Toutes les zones" : "All zones"}</option>{MARKET_ZONES.map((z) => <option key={z.value} value={z.value}>{z[lang]}</option>)}</select>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        {loading ? <p className="py-16 text-center text-anthracite/40">…</p> : byZone.length === 0 ? (
          <div className="border border-dashed border-tertiary bg-white py-20 text-center" data-testid="markets-empty">
            <Globe2 className="mx-auto mb-3 h-6 w-6 text-anthracite/30" />
            <p className="text-anthracite/50">{lang === "fr" ? "Aucun marché publié pour ces critères." : "No published market for these criteria."}</p>
            <Link to="/connect?category=markets" className="mt-4 inline-block bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory hover:bg-black">{lang === "fr" ? "Demander une couverture marché" : "Request market coverage"}</Link>
          </div>
        ) : byZone.map((g) => (
          <div key={g.zone.value} className="mb-12">
            <h2 className="mb-5 font-serif text-2xl text-anthracite">{g.zone[lang]}</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {g.list.map((m) => (
                <Link key={m.id} to={`/marches/${m.slug}`} data-testid={`market-card-${m.slug}`} className="group flex flex-col border border-tertiary bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-1 flex-col p-6">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-anthracite/40"><MapPin className="h-3.5 w-3.5" />{m.region || g.zone[lang]}</span>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[10px] ${coverageCls[m.coverage_level]}`}>{cov[m.coverage_level]?.[lang]}</span>
                    </div>
                    <h3 className="mb-2 font-serif text-2xl leading-snug text-anthracite">{L(m, "country_name") || m.country}</h3>
                    <p className="mb-4 flex-1 text-sm leading-relaxed text-anthracite/70 line-clamp-3">{L(m, "summary")}</p>
                    {(m.priority_sectors || []).length > 0 && <div className="flex flex-wrap gap-1.5 border-t border-tertiary pt-4">{m.priority_sectors.slice(0, 3).map((s) => <span key={s} className="rounded-full bg-sand px-2 py-0.5 text-[10px] text-anthracite/60">{s}</span>)}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
